import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getGaTagforL1L2 } from "@NA/Utils/GtmUtility";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CategoryDetailsPageContext } from "@insite/content-library/Pages/CategoryDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Dictionary } from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

interface CustomPromoCodeModel {
    title: string;
    imagePath: string;
    redirectUrl: string;
}

interface CustomPromoCode {
    sale: CustomPromoCodeModel;
    clearance: CustomPromoCodeModel;
}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryName?: TypographyProps;
    customBlock?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 5,
        css: css`
            & ${GridOffset} {
                justify-content: center;
                margin-top: 15px;
            }
        `,
    },
    subCategoryItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        flex-basis: 11.666667% !important;
                        max-width: 11.666667% !important;
                        justify-content: center;
                    `,
                    css`
                        flex-basis: 11.666667% !important;
                        max-width: 11.666667% !important;
                        justify-content: center;
                    `,
                ])}
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 31px;
            justify-content: center;
            ${({ theme }) => { return breakpointMediaQueries(
                    theme, [null, css` padding: 10px 0; `,],"max",);
            }}
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                width: 94.25px;
                height: 100px;
                object-fit: contain;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            @media (min-width: 768px) and (max-width: 1080px) {
                padding: 0px 17px;
            }
            @media (min-width: 1081px) {
                padding: 0px 5px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 5px;
                    `,
                    css`
                        padding: 0px 5px;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;                        
                    `,
                    css`
                        justify-content: center;                        
                    `,
                ])}
        `,
    },

    subCategoryName: {
        weight: "bold",
        css: css`
            font-size: 15px; // Remove if have same size in style guide
            font-weight: bold;
            line-height: normal;
            margin-top: 31px;
            text-align: center;
            width: 100%;
            text-align: center;
            word-wrap: break-word;
            letter-spacing: normal;
            color: ${getColor("text")};
            margin: 0;
            width: 100%;
            min-height: auto;
            padding: 0;
            ${({ theme }) => { return breakpointMediaQueries(
                    theme, [null, css` font-size: 14px;  `,],"max",);
            }}
            :hover {
                text-decoration: underline;
            }
        `,
    },
    customBlock: {
        css: css`
            justify-content: center;
            flex-basis: 11.66667%;
            max-width: 11.66667%;
            img {
                object-fit: contain;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 33.333333%;
                        max-width: 33.333333%;
                    `,
                    css`
                        flex-basis: 33.333333%;
                        max-width: 33.333333%;
                    `,
                    css`
                        flex-basis: 16.666667%;
                        max-width: 16.666667%;
                    `,
                    css`
                        flex-basis: 11.66667%;
                        max-width: 11.66667%;
                    `,
                    css`
                        flex-basis: 11.66667%;
                        max-width: 11.66667%;
                    `,
                ])}
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({
    category,
    promoBannerSettings,
    fields: { showImages },
}: Props & ReturnType<typeof mapStateToProps>) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    const customPomoCodeData = GetSaleClearanceCategory(
        category.urlSegment,
        promoBannerSettings.cdnPathCPB,
        promoBannerSettings,
    );

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem} >
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
            {customPomoCodeData.sale.redirectUrl && (
                <GridItem {...styles.customBlock}>
                    <CustomPromoBanner showImages={showImages} customPromoCode={customPomoCodeData.sale} />
                </GridItem>
            )}
            {customPomoCodeData.clearance.redirectUrl && (
                <GridItem {...styles.customBlock}>
                    <CustomPromoBanner showImages={showImages} customPromoCode={customPomoCodeData.clearance} />
                </GridItem>
            )}
        </GridContainer>
    );
};

const CustomPromoBanner = ({
    showImages,
    customPromoCode,
}: {
    showImages: boolean;
    customPromoCode: CustomPromoCodeModel;
}) => {
    if (customPromoCode.redirectUrl.trim() === "") {
        return null;
    }

    return (
        <Link
            href={customPromoCode.redirectUrl}
            data-test-selector={`categoryDetailsSubCategoriesLink_${Math.random()}`}
            data-ga-promo={getGaTagforL1L2(customPromoCode.title,"","")}
        >
            <GridContainer {...styles.innerContainer}>
                {showImages && (
                    <GridItem {...styles.subCategoryImageItem} >
                        {customPromoCode.imagePath && (
                            <LazyImage
                                src={customPromoCode.imagePath}
                                altText={customPromoCode.title}
                                {...styles.subCategoryImage}
                            />
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.subCategoryNameLinkItem}>
                    <Typography {...styles.subCategoryName} variant="h3">
                        {customPromoCode.title}
                    </Typography>
                </GridItem>
            </GridContainer>
        </Link>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}  data-ga-promo={getGaTagforL1L2(category.shortDescription,"","")}>
            <GridContainer {...styles.innerContainer}>
                {showImages && (
                    <GridItem {...styles.subCategoryImageItem}  >
                        {category.smallImagePath && (
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.subCategoryNameLinkItem}>
                    <Typography {...styles.subCategoryName} variant="h3" >
                        {category.shortDescription}
                    </Typography>
                </GridItem>
            </GridContainer>
        </Link>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
    promoBannerSettings: (state.context.settings.settingsCollection as any).catalogSettingSettings,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryDetailSubCategories)),
    definition: {
        group: "Categories",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: [CategoryDetailsPageContext],
    },
};

export default widgetModule;

const ReturnCleranceSettingValue = (settingValue: string): string[] => {
    return settingValue.trim() !== "" ? settingValue.split("|") : [];
};

const PopulateCleranceSaleModel = (categoryValues: string[], cdnPath: string): CustomPromoCodeModel => {
    const categoryResponseObj: CustomPromoCodeModel = {
        title: categoryValues[0],
        imagePath: cdnPath + categoryValues[1],
        redirectUrl: categoryValues[2],
    };

    return categoryResponseObj;
};

// to get a sale and clearance tag
const GetSaleClearanceCategory = (
    catUrlSegment: string,
    cdnPath: string,
    promoBannerSettings: any,
): CustomPromoCode => {
    const categoryDictionary: Dictionary<string> = {
        "video-surveillance": "videoSurveillanceCPB,videoSurveillanceCPB1",
        "access-control": "accessControlCPB,accessControlCPB1",
        "residential-av": "residentialAVCPB,residentialAVCPB1",
        "pro-av": "proAVCPB,proAVCPB1",
        fire: "fireCPB,fireCPB1",
        intrusion: "intrusionCPB,intrusionCPB1",
        "smart-home": "smartHomeCPB,smartHomeCPB1",
        communications: "communicationsCPB,communicationsCPB1",
        "wire-cable": "wireCableCPB,wireCableCPB1",
        "data-communications-networking": "dataCommNetworkingCPB,dataCommNetworkingCPB1",
        "batteries-power-supplies": "batteriesPowerSuppliesCPB,batteriesPowerSuppliesCPB1",
        "tools-hardware": "toolsHardwareCPB,toolsHardwareCPB1",
        "central-vacuum": "centralVacuumCPB,centralVacuumCPB1",
    };

    let saleCategoryObj: CustomPromoCodeModel = {
        title: "",
        imagePath: "",
        redirectUrl: "",
    };

    let clearanceCategoryObj: CustomPromoCodeModel = {
        title: "",
        imagePath: "",
        redirectUrl: "",
    };

    let clearanceObj: CustomPromoCode = {
        sale: saleCategoryObj,
        clearance: clearanceCategoryObj,
    };
    if (categoryDictionary[catUrlSegment] === undefined) {
        return clearanceObj;
    }

    const possibleSettingValues = categoryDictionary[catUrlSegment].split(",");
    if (possibleSettingValues.length > 0) {
        clearanceCategoryObj = PopulateCleranceSaleModel(
            ReturnCleranceSettingValue(promoBannerSettings[possibleSettingValues[0]]),
            cdnPath,
        ); // clearance Banner
        saleCategoryObj = PopulateCleranceSaleModel(
            ReturnCleranceSettingValue(promoBannerSettings[possibleSettingValues[1]]),
            cdnPath,
        ); // sale Banner
    }

    clearanceObj = {
        sale: saleCategoryObj,
        clearance: clearanceCategoryObj,
    };

    return clearanceObj;
};
